// Relative imports (not `@/`) so bun can test this module.
import { getHexTileMapKey } from '../hexmap/model/keys';
import type { RuleSet } from '../rules';
import {
  canHeal,
  type EntityId,
  healTarget,
  isStarving,
  type SimContext,
  type SimState,
  TEND_LEASH,
  TEND_RADIUS,
  tendTarget,
  toMilli,
  type Unit,
  unitHealRate,
  unitMaxHp,
} from '../sim';

/** Sim seconds per tick. */
const DT = 0.1;

/** Mint order of an entity id ("u12" → 12), as the sim orders its units. */
function mint(id: EntityId): number {
  return Number(id.slice(1));
}

/**
 * Healer id → the unit it's mending, for every heal the sim's heal step
 * would make from `state` (the heal counterpart of `engagements`): healers
 * outside buildings, oldest first, each on the own unit of its kind in range
 * missing the largest share of its hp (`healTarget`), on hp as the earlier
 * healers leave it, so two healers beside two wounded units show one each.
 * Runs on copies; `state` is untouched.
 */
export function healings(state: SimState, rules: RuleSet): Map<EntityId, EntityId> {
  const result = new Map<EntityId, EntityId>();
  const units = Object.values(state.units)
    .filter((u) => !u.inside)
    .sort((a, b) => mint(a.id) - mint(b.id) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const healers = units.filter((u) => canHeal(rules, u));
  if (healers.length === 0) return result;

  const at = new Map<string, Unit[]>();
  for (const u of units) {
    const copy = { ...u };
    const key = getHexTileMapKey(u.pos);
    const list = at.get(key);
    if (list) list.push(copy);
    else at.set(key, [copy]);
  }
  for (const healer of healers) {
    const patient = healTarget(rules, healer, at);
    if (!patient) continue;
    const amount = Math.min(
      toMilli(unitHealRate(rules, healer) * DT),
      unitMaxHp(rules, patient) - patient.hp
    );
    if (amount <= 0) continue;
    patient.hp += amount;
    result.set(healer.id, patient.id);
  }
  return result;
}

/** "heal" for a healer of people and horses, "repair" for one of machines (engineers). */
export function healVerb(rules: RuleSet, type: string): 'heal' | 'repair' {
  return rules.units[type]?.heals === 'mechanical' ? 'repair' : 'heal';
}

/** The activity icons over a unit that's healing / repairing right now. */
export const HEAL_ICON = '✚';
export const REPAIR_ICON = '🔧';

/**
 * "Heals 2.2 hp/s · range 1 · people and horses" (engineers: "Repairs … · machines"): what a selected healer
 * does, its rate with its rank's bonus (`unitHealRate`); null for a unit that
 * doesn't heal.
 */
export function healRateText(rules: RuleSet, unit: Unit): string | null {
  if (!canHeal(rules, unit)) return null;
  const def = rules.units[unit.type];
  const rate = unitHealRate(rules, unit);
  const verb = healVerb(rules, unit.type) === 'repair' ? 'Repairs' : 'Heals';
  const whom = def?.heals === 'mechanical' ? 'machines' : 'people and horses';
  return `${verb} ${Number(rate.toFixed(1))} hp/s · range ${def?.range ?? 1} · ${whom}`;
}

/** A healer's status for the roster and the unit bar: a short word and the full line. */
export interface HealerStatus {
  /** "healing", "going to heal", "looking for wounded", "idle" (for tallies). */
  activity: string;
  /** "healing warrior", "going to heal archer", "idle: no one hurt nearby". */
  text: string;
}

/**
 * What an own healer outside a building is doing, as the sim's tend and
 * heal steps see it: mending someone this tick (`healing`, from `healings`),
 * walking to the patient it picked (a `heal` order, tend.ts), about to pick
 * one (free, and a wounded unit it would tend is within `TEND_RADIUS` of
 * where it looks: its group leader, else itself), or idle (nobody hurt
 * nearby, or no food: a starving player's healers don't heal). Null for
 * anything else (not a healer, inside a building, automated, on another
 * order, walking a move of its own, or following its group with nobody to
 * tend), so the usual status shows.
 */
export function healerStatus(
  state: SimState,
  ctx: SimContext,
  unit: Unit,
  healing: ReadonlyMap<EntityId, EntityId>
): HealerStatus | null {
  const { rules } = ctx;
  if (!canHeal(rules, unit) || unit.inside || unit.auto) return null;
  const repair = healVerb(rules, unit.type) === 'repair';
  const verb = repair ? 'repair' : 'heal';
  const nameOf = (id: EntityId | undefined) => {
    const u = state.units[id ?? ''];
    return (u ? (rules.units[u.type]?.name ?? u.type) : 'unit').toLowerCase();
  };
  const mending = healing.get(unit.id);
  if (mending !== undefined) {
    const activity = repair ? 'repairing' : 'healing';
    return { activity, text: `${activity} ${nameOf(mending)}` };
  }
  if (isStarving(state, rules, unit.owner)) {
    return { activity: 'idle', text: `idle: no food to ${verb}` };
  }
  const looking = repair ? 'looking for repairs' : 'looking for wounded';
  if (unit.order?.kind === 'heal') {
    // Gone or mended: tend picks the next on its next pass.
    if (!state.units[unit.order.targetId]) return { activity: looking, text: looking };
    return {
      activity: `going to ${verb}`,
      text: `going to ${verb} ${nameOf(unit.order.targetId)}`,
    };
  }
  if (unit.order) return null;
  const leaderId = unit.groupId !== undefined ? state.groups?.[unit.groupId]?.leader : undefined;
  const leader = leaderId !== undefined && leaderId !== unit.id ? state.units[leaderId] : undefined;
  const anchor = leader && !leader.inside ? leader : undefined;
  // Walking a move of its own: the usual "moving".
  if (!anchor && unit.path && unit.path.length > 0) return null;
  const units = Object.values(state.units);
  if (
    tendTarget(ctx, unit, (anchor ?? unit).pos, units, undefined, anchor ? TEND_LEASH : TEND_RADIUS)
  )
    return { activity: looking, text: looking };
  // Following its group with nobody to tend: the usual "moving".
  if (unit.path && unit.path.length > 0) return null;
  const nobody = repair ? 'nothing damaged nearby' : 'no one hurt nearby';
  return { activity: 'idle', text: `idle: ${nobody}` };
}
